// address.jsx - One text box that finishes somebody's address for them.
//
// The problem it solves is not typing. It is that a surprising number of people
// do not know their own address: the street with no number, the township the
// post office calls something else, last year's ZIP. Every one of those is a
// mailer that goes nowhere and a phone call to work out why.
//
// So it is a normal Input first and a lookup second. Somebody can ignore the
// list entirely and type the whole thing, which is what happens when the key is
// missing, when Google is slow, and when the address is too new to be in there.
// Nothing here is required and nothing blocks on a request.
//
// Everything goes through our own server. There is no Google script on the page
// and no key in this bundle. See server/src/lib/places.js for why.
//
// LEGEND
//   1. useSession()      one billing session per address, not per keystroke
//   2. AddressField      the input, the list, and the keyboard
//   3. PoweredByGoogle   required wherever predictions are shown without a map

import { useCallback, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";

import { publicApi } from "../lib/api.js";
import { Input } from "./kit.jsx";

////////// 1. THE SESSION //////////
// Google bills a session, not a request: every suggestion somebody sees while
// typing one address, plus the one lookup of what they picked, is one charge.
// A new token starts a new session, so it is minted once and thrown away the
// moment an address is chosen.

const newSession = () =>
  globalThis.crypto?.randomUUID?.() || `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

function useSession() {
  const token = useRef(newSession());
  const restart = useCallback(() => { token.current = newSession(); }, []);
  return [token, restart];
}

////////// 2. THE FIELD //////////
// `onPick` gets the whole address at once: line1, line2, city, state, postal.
// A caller sets its four or five fields and never learns what a place id is.
//
// `value` and `onChange` are the ordinary controlled pair, so this drops into a
// form exactly where an Input was and behaves the same when nothing is
// configured.

export function AddressField({ value, onChange, onPick, label = "Street address", hint, ...rest }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [session, restartSession] = useSession();
  const listId = useId();

  // The last thing a person picked. Retyping it should not reopen the list on
  // the address they have already chosen.
  const chosen = useRef("");
  const box = useRef(null);

  useEffect(() => {
    const query = String(value || "").trim();
    if (query.length < 3 || query === chosen.current) {
      setSuggestions([]);
      return undefined;
    }

    // Typing is faster than a network. A request per keystroke would show a
    // list two letters behind the box and bill for every one of them.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      publicApi.suggestAddress(query, session.current, controller.signal)
        .then((data) => {
          setSuggestions(data.suggestions || []);
          setActive(-1);
          setOpen((data.suggestions || []).length > 0);
        })
        // Aborted, offline, not configured. The box is still a box.
        .catch(() => setSuggestions([]));
    }, 220);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [value, session]);

  // A click anywhere else closes the list. Blur alone cannot do this: the click
  // that lands on a suggestion fires blur first and would close it before the
  // pick was registered.
  useEffect(() => {
    if (!open) return undefined;
    const away = (event) => { if (!box.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const pick = async (suggestion) => {
    setOpen(false);
    chosen.current = suggestion.line;
    onChange(suggestion.line);
    try {
      const { address } = await publicApi.addressDetail(suggestion.id, session.current);
      chosen.current = address.line1 || suggestion.line;
      onPick?.(address);
    } catch {
      // The suggestion text is already in the box, so somebody is no worse off
      // than before they picked. They finish the rest by hand.
    } finally {
      // That address is bought and paid for. Anything typed next is a new one.
      restartSession();
    }
  };

  const onKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter" && active >= 0) {
      // Only when something is highlighted. Enter with nothing chosen belongs to
      // the form, which is how somebody who typed their whole address moves on.
      event.preventDefault();
      pick(suggestions[active]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={box} className="relative">
      <Input
        label={label}
        hint={hint}
        // Off, because the browser's own list and this one would sit on top of
        // each other and the wrong one wins depending on the browser.
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        {...rest}
      />

      {/* data-private keeps this out of a session replay. Every input value is
          masked, but a suggestion is rendered text, and it is somebody's real
          street address. See analytics.js section 6. */}
      {open && suggestions.length > 0 && (
        <div
          data-private
          id={listId}
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 bg-paper border border-rule shadow-pop rounded-sm overflow-hidden"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              role="option"
              aria-selected={index === active}
              // mousedown rather than click: the input's blur fires first on a
              // click and the list would be gone before the pick landed.
              onMouseDown={(event) => { event.preventDefault(); pick(suggestion); }}
              onMouseEnter={() => setActive(index)}
              className={clsx(
                "block w-full text-left px-3.5 py-2.5 border-b border-rule-soft last:border-0",
                index === active ? "bg-surface" : "bg-paper"
              )}
            >
              <span className="block text-[14px] text-ink">{suggestion.line}</span>
              {suggestion.detail && <span className="block text-[12.5px] text-muted mt-0.5">{suggestion.detail}</span>}
            </button>
          ))}
          <PoweredByGoogle />
        </div>
      )}
    </div>
  );
}

////////// 2b. THE SAME THING, WIRED TO A FORM //////////
// Every address in this codebase sits in a useForm under some prefix, so this
// is the whole wiring: one line at the call site, and the surrounding city,
// state and ZIP inputs stay exactly where each screen already puts them.
//
// A field the screen does not draw is still filled. `line2` is on the model
// everywhere it matters and a flat number that reached us should be printed on
// the label whether or not somebody was shown a box for it.

export function FormAddressField({ form, prefix = "address", ...rest }) {
  return (
    <AddressField
      value={prefix.split(".").reduce((cursor, key) => cursor?.[key], form.values)?.line1}
      onChange={(next) => form.set(`${prefix}.line1`, next)}
      onPick={(address) => {
        for (const key of ["line1", "line2", "city", "state", "postal"]) {
          if (address[key]) form.set(`${prefix}.${key}`, address[key]);
        }
      }}
      error={form.errors[`${prefix}.line1`]}
      {...rest}
    />
  );
}

////////// 3. ATTRIBUTION //////////
// Google's terms require this wherever their predictions are shown and there is
// no Google map on the page to carry it. There is no map on any page here, so
// it goes on every list. Do not remove it to tidy up the dropdown.

function PoweredByGoogle() {
  return (
    <p className="px-3.5 py-1.5 text-[11px] text-faint bg-surface/60 border-t border-rule-soft">
      Addresses by Google
    </p>
  );
}
